import { ButtonAlmostThere } from '@/components/sections/builder/elements/body/customer/customer_order/component/button-almost-there';
import { CustomerOrderElementDataType } from '@/components/sections/builder/elements/body/customer/customer_order/type';
import { Button } from '@/components/ui/button';
import useModal from '@/hooks/useModal';
import { cn, isEmpty } from '@/lib/utils';
import { Printer, Truck, Wallet } from 'lucide-react';

type OrderActionButtonProps = {
  status: string;
  content: CustomerOrderElementDataType;
  handlePayment: () => void;
  handleTrackOrder: () => void;
  handleInvoice: () => void;
  shippingType: 'shipping' | 'pickup_in_store';
  orderId?: string;
  setOpenModal?: (open: boolean) => void;
  modalNotify?: ReturnType<typeof useModal>;
};
export default function OrderActionButton({
  status,
  content,
  handlePayment,
  handleTrackOrder,
  handleInvoice,
  shippingType,
  orderId,
  setOpenModal,
  modalNotify,
}: OrderActionButtonProps) {
  const colorScheme = content?.colorScheme;

  const buttonsByStatus: Record<string, React.ReactNode> = {
    unpaid: (
      <>
        <StyledButton
          onClick={handleInvoice}
          testId='invoice-button-order-customer'
          colorScheme={colorScheme}
        >
          <Printer className='h-4 w-4' /> Invoice
        </StyledButton>
        <StyledButton
          onClick={handlePayment}
          testId='pay-button-order-customer'
          colorScheme={colorScheme}
        >
          <Wallet className='h-4 w-4' /> Pay
        </StyledButton>
      </>
    ),
    not_yet_shipped: (
      <>
        <StyledButton
          onClick={handleInvoice}
          testId='invoice-button-order-customer'
          colorScheme={colorScheme}
        >
          <Printer className='h-4 w-4' /> Invoice
        </StyledButton>
        <StyledButton
          onClick={handleTrackOrder}
          testId='track-button-order-customer'
          colorScheme={colorScheme}
        >
          <Truck className='h-4 w-4' /> Track Order
        </StyledButton>
      </>
    ),
    shipping: (
      <>
        <StyledButton
          onClick={handleInvoice}
          testId='invoice-button-order-customer'
          colorScheme={colorScheme}
        >
          <Printer className='h-4 w-4' /> Invoice
        </StyledButton>
        <StyledButton
          onClick={handleTrackOrder}
          testId='track-button-order-customer'
          colorScheme={colorScheme}
        >
          <Truck className='h-4 w-4' /> Track Order
        </StyledButton>
      </>
    ),
    shipped: (
      <>
        <StyledButton
          onClick={handleInvoice}
          testId='invoice-button-order-customer'
          colorScheme={colorScheme}
        >
          <Printer className='h-4 w-4' /> Invoice
        </StyledButton>
        <StyledButton
          onClick={handleTrackOrder}
          testId='track-button-order-customer'
          colorScheme={colorScheme}
        >
          <Truck className='h-4 w-4' /> Track Order
        </StyledButton>
      </>
    ),
    paid: (
      <>
        <StyledButton
          onClick={handleInvoice}
          testId='invoice-button-order-customer'
          colorScheme={colorScheme}
        >
          <Printer className='h-4 w-4' /> Invoice
        </StyledButton>
        {shippingType === 'pickup_in_store' && modalNotify && (
          <ButtonAlmostThere
            colorScheme={colorScheme}
            orderId={orderId}
            setOpenModal={setOpenModal}
            modalNotify={modalNotify}
          />
        )}
      </>
    ),
    ready_to_process: (
      <>
        <StyledButton
          onClick={handleInvoice}
          testId='invoice-button-order-customer'
          colorScheme={colorScheme}
        >
          <Printer className='h-4 w-4' /> Invoice
        </StyledButton>
        {shippingType === 'pickup_in_store' && modalNotify && (
          <ButtonAlmostThere
            colorScheme={colorScheme}
            orderId={orderId}
            setOpenModal={setOpenModal}
            modalNotify={modalNotify}
          />
        )}
      </>
    ),
    processing: (
      <>
        <StyledButton
          onClick={handleInvoice}
          testId='invoice-button-order-customer'
          colorScheme={colorScheme}
        >
          <Printer className='h-4 w-4' /> Invoice
        </StyledButton>
        {shippingType === 'pickup_in_store' && modalNotify && (
          <ButtonAlmostThere
            colorScheme={colorScheme}
            orderId={orderId}
            setOpenModal={setOpenModal}
            modalNotify={modalNotify}
          />
        )}
      </>
    ),
    cancel: (
      <StyledButton onClick={handleInvoice} colorScheme={colorScheme}>
        <Printer className='h-4 w-4' /> Invoice
      </StyledButton>
    ),
    cancelled: (
      <StyledButton onClick={handleInvoice} colorScheme={colorScheme}>
        <Printer className='h-4 w-4' /> Invoice
      </StyledButton>
    ),
    done: (
      <StyledButton onClick={handleInvoice} colorScheme={colorScheme}>
        <Printer className='h-4 w-4' /> Invoice
      </StyledButton>
    ),
    expired: (
      <StyledButton onClick={handleInvoice} colorScheme={colorScheme}>
        <Printer className='h-4 w-4' /> Invoice
      </StyledButton>
    ),
  };

  return <div className={divClass}>{buttonsByStatus[status] ?? null}</div>;
}

const baseButtonClass = 'w-full gap-2 px-12 py-5 sm:w-full text-sm';
const divClass =
  'flex flex-col sm:justify-end items-start w-full gap-2 font-normal sm:flex-row sm:items-center sm:gap-1';

const getButtonStyle = (colorScheme?: CustomerOrderElementDataType['colorScheme']) =>
  isEmpty(colorScheme)
    ? {}
    : {
        backgroundColor: colorScheme?.btnSolidBackgroud,
        color: colorScheme?.btnSolidlabel,
      };

const StyledButton = ({
  onClick,
  children,
  testId,
  colorScheme,
}: {
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
  colorScheme?: CustomerOrderElementDataType['colorScheme'];
}) => (
  <Button
    type='button'
    className={cn(baseButtonClass, isEmpty(colorScheme) && 'theme_button')}
    style={getButtonStyle(colorScheme)}
    data-testid={testId}
    onClick={onClick}
  >
    {children}
  </Button>
);
